import { inject, Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { RadioOptionModel } from '@pinup-teams/common';
import { forkJoin } from 'rxjs';

import { ChatMessageFeedbackReasons } from '../models';

@Injectable({
  providedIn: 'root',
})
export class DictionariesService {
  #translateService = inject(TranslateService);

  #supportedLanguages = ['en', 'ru', 'uk', 'az', 'tr', 'es'];

  #translations: Record<string, Record<string, string>> =
    this.#supportedLanguages.reduce(
      (acc, lang) => ({ ...acc, [lang]: {} }),
      {},
    );

  dislikeReasons: RadioOptionModel[] = [
    {
      value: ChatMessageFeedbackReasons.inappropriate,
      label: this.#translateService.instant('dislikeReasons.inappropriate'),
    },
    {
      value: ChatMessageFeedbackReasons.wrong,
      label: this.#translateService.instant('dislikeReasons.wrong'),
    },
    {
      value: ChatMessageFeedbackReasons.unacceptable,
      label: this.#translateService.instant('dislikeReasons.unacceptable'),
    },
    {
      value: ChatMessageFeedbackReasons.other,
      label: this.#translateService.instant('dislikeReasons.other'),
    },
  ];

  constructor() {
    this.#loadTranslations();
  }

  translate(
    key: string,
    lang: string,
    interpolation?: { [key: string]: any },
  ): string {
    const translateToLanguage =
      lang || this.#translateService.currentLang || 'en';

    let translatedText = translateToLanguage
      ? this.#translations[translateToLanguage][key]
      : key;

    if (interpolation) {
      Object.keys(interpolation).forEach((variable) => {
        const regex = new RegExp(`{{${variable}}}`, 'g');
        translatedText = translatedText.replace(regex, interpolation[variable]);
      });
    }

    return translatedText;
  }

  addTranslations(translations: Record<string, string>, langCode: string) {
    this.#translations[langCode] = {
      ...this.#translations[langCode],
      ...translations,
    };
  }

  #loadTranslations() {
    forkJoin(
      this.#supportedLanguages.map((lang) =>
        this.#translateService.getTranslation(lang),
      ),
    ).subscribe((translationsArray) => {
      this.#translations = this.#supportedLanguages.reduce(
        (acc, lang, i) => ({
          ...acc,
          [lang]: {
            ...acc[lang],
            ...translationsArray[i],
          },
        }),
        this.#translations,
      );
    });
  }
}
